import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { EASE, requestPilot, scrollToId } from "./Reveal";

const LINKS = [
  { label: "What we do", href: "#what-we-do" },
  { label: "The Group", href: "#group" },
  { label: "Entities", href: "#entities" },
  { label: "Advantage", href: "#advantage" },
  { label: "Footprint", href: "#footprint" },
  { label: "Engage", href: "#engage" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
      className={`fixed inset-x-0 top-0 z-[80] border-b transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "border-white/10 bg-surface/80 backdrop-blur-xl"
          : "border-transparent bg-surface/40 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
          className="flex items-center gap-2.5"
        >
          <img src="/logo.png" alt="Dignifyd Group" className="h-6 w-auto sm:h-7" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gray-400">
            Group
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="header-nav">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => go(l.href)}
              className="group relative font-mono text-xs uppercase tracking-[0.2em] text-gray-300 transition-colors duration-300 hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="book-pilot-cta"
            onClick={() => requestPilot()}
            className="group hidden items-center gap-2 bg-gold px-5 py-2.5 font-mono text-xs font-medium uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-blue-300 sm:flex"
          >
            Book a 60-day pilot
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="p-2 text-white lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden border-t border-white/10 bg-surface/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => go(l.href)}
                  className="py-3 text-left font-heading text-lg font-medium text-gray-200 transition-colors duration-300 hover:text-gold"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-book-pilot-cta"
                onClick={() => {
                  setOpen(false);
                  requestPilot();
                }}
                className="mt-2 bg-gold px-5 py-3 text-left font-mono text-xs font-medium uppercase tracking-[0.15em] text-ink"
              >
                Book a 60-day pilot
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
